import logger from '../lib/logger';
// API Service for Fatimia Community Services
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function for API calls
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}/api${endpoint}`;
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  try {
    const response = await fetch(url, { ...defaultOptions, ...options });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    logger.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

// ============ Public APIs ============

// Prayer Times
export const fetchPrayerTimes = async () => {
  return apiCall('/prayer-times');
};

// Events
export const fetchEvents = async (eventType = null) => {
  const endpoint = eventType ? `/events?event_type=${eventType}` : '/events';
  return apiCall(endpoint);
};

// Donation Config
export const fetchDonationConfig = async () => {
  return apiCall('/donations/config');
};

// Site Settings
export const fetchSiteSettings = async () => {
  return apiCall('/settings');
};

// ============ Form Submissions ============

// Contact Form
export const submitContactForm = async (data) => {
  return apiCall('/contact', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Volunteer Sign-up
export const submitVolunteerForm = async (data) => {
  return apiCall('/volunteer', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Madrassah Registration
export const submitMadrassahForm = async (data) => {
  return apiCall('/madrassah', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Youth Registration
export const submitYouthForm = async (data) => {
  return apiCall('/youth', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Donation
export const submitDonation = async (data) => {
  return apiCall('/donations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// ============ Admin APIs ============

// Admin Login
export const adminLogin = async (credentials) => {
  return apiCall('/admin/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
};

// Helper to add auth header
const authApiCall = async (endpoint, options = {}, token) => {
  return apiCall(endpoint, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    },
  });
};

// Admin Stats
export const fetchAdminStats = async (token) => {
  return authApiCall('/admin/stats', {}, token);
};

// Admin Events
export const fetchAdminEvents = async (token) => {
  return authApiCall('/admin/events', {}, token);
};

export const createEvent = async (token, data) => {
  return authApiCall('/admin/events', {
    method: 'POST',
    body: JSON.stringify(data),
  }, token);
};

export const updateEvent = async (token, eventId, data) => {
  return authApiCall(`/admin/events/${eventId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }, token);
};

export const deleteEvent = async (token, eventId) => {
  return authApiCall(`/admin/events/${eventId}`, {
    method: 'DELETE',
  }, token);
};

// Admin Contacts
export const fetchAdminContacts = async (token) => {
  return authApiCall('/admin/contacts', {}, token);
};

export const markContactRead = async (token, contactId) => {
  return authApiCall(`/admin/contacts/${contactId}/read`, {
    method: 'PUT',
  }, token);
};

// Admin Volunteers
export const fetchAdminVolunteers = async (token) => {
  return authApiCall('/admin/volunteers', {}, token);
};

// Admin Donations
export const fetchAdminDonations = async (token) => {
  return authApiCall('/admin/donations', {}, token);
};

export const updateDonationStatus = async (token, donationId, status) => {
  return authApiCall(`/admin/donations/${donationId}/status?status=${status}`, {
    method: 'PUT',
  }, token);
};
